// Typed REST client for chatboard's forecast surface.
//
// Each method wraps `lib/apiClient` (credentials + centralized 401→login + never-throws) and
// returns a TYPED domain result, so the raw `{ok,status,body:any}` envelope never leaks into
// callers — the edge cases a thin passthrough pushed onto every call site (2xx-with-null-body,
// 404-recovery, parse failure) are handled ONCE, here. Callers can inject a typed fake
// instead of stubbing `globalThis.fetch`.
import { validatePmf, type OutcomeSpace } from "@sooth-labs/idl";
import { apiGet, apiPost, apiDelete } from "../lib/apiClient.js";
import type { ForecastResult } from "../types/forecast.js";
import type { ForecastWire, SharedForecastWire } from "../types/forecastWire.js";
import type { SavableForecast } from "../types/persistence.js";
import { chatTurnHeaders } from "../lib/chatTurn.js";

/**
 * The structured contract preprocess returns (contract-server `POST /preprocess`). Typed to at
 * least the fields `contractFromPreprocessed` reads; the index signature keeps it forward-compat
 * with server additions without weakening those known fields to `any`. (No generated type yet —
 * preprocess lives on the contract server, outside the forecast-server OpenAPI.)
 */
export interface PreprocessedContract {
  question_text?: string;
  title?: string;
  scheduled_resolution_time?: string | null;
  resolution_criteria?: string;
  // MW: mode only (POST /preprocess/multi) — the authored answer space + its render-ready
  // bucket labels. Absent on the ordinary binary contract (and on an MW: degrade-to-binary).
  outcome_space?: Record<string, unknown>;
  outcome_labels?: string[];
  outcome_kind?: string;
  [k: string]: unknown;
}

/**
 * The user intent a request belongs to (lib/telemetry/intentDeps.ts merges it in): sent as
 * `x-intent-id` by lib/apiClient.js.
 */
export interface IntentOptions {
  intentId?: string | null;
}

/** `ok:false` on non-2xx OR a null body (2xx non-JSON), so a bad response fails the load
 *  instead of silently dropping rows.
 *
 *  `view:"lean"` asks for the slim projection (identity, topology, list/badge fields and the
 *  lifecycle columns — no contract/events/server_expl/full server_ctx). `lean` is echoed from
 *  the response's `view` field, NOT assumed from the request, so a server that predates the
 *  param (or ignores it) is correctly treated as having returned full rows. */
export async function listForecasts(opts: { view?: "full" | "lean" } = {}): Promise<{
  ok: boolean;
  forecasts: ForecastWire[];
  pinned: Record<string, unknown>;
  lean: boolean;
}> {
  const q = opts.view === "lean" ? "?status=all&view=lean" : "?status=all";
  const r = await apiGet(`/api/forecasts${q}`);
  if (!r.ok || r.body == null) return { ok: false, forecasts: [], pinned: {}, lean: false };
  const d = r.body as {
    forecasts?: ForecastWire[];
    pinned?: Record<string, unknown>;
    view?: string;
  };
  return {
    ok: true,
    forecasts: d.forecasts || [],
    pinned: d.pinned || {},
    lean: d.view === "lean",
  };
}

/**
 * How many contract keys one `GET /api/forecasts/by-contract` request may carry. Must equal
 * `MAX_QKEYS` in experimental/chatboard/server/lib/qkeyFilter.js, beyond which the server
 * rejects the request.
 */
export const BY_CONTRACT_MAX_QKEYS = 100;

/**
 * Every full row the viewer can read on `qkeys` (at most BY_CONTRACT_MAX_QKEYS): the owner's
 * rows and the rows shared with them, in one array. `ok:false` on a non-2xx or a null body.
 */
export async function listForecastsByContract(qkeys: string[]): Promise<{
  ok: boolean;
  forecasts: ForecastWire[];
}> {
  const params = new URLSearchParams();
  for (const k of qkeys) params.append("qkey", k);
  const r = await apiGet(`/api/forecasts/by-contract?${params.toString()}`);
  if (!r.ok || r.body == null) return { ok: false, forecasts: [] };
  const d = r.body as { forecasts?: unknown };
  return { ok: true, forecasts: Array.isArray(d.forecasts) ? (d.forecasts as ForecastWire[]) : [] };
}

/**
 * How many forecasts one `GET /api/forecasts/by-id` request may name. Must equal `MAX_IDS` in
 * experimental/chatboard/server/lib/qkeyFilter.js, beyond which the server rejects the request.
 */
export const BY_ID_MAX_IDS = 100;

/**
 * The full rows of the forecasts `ids` names (at most BY_ID_MAX_IDS), the owner's and the ones
 * shared with the viewer, in one array. `ok:false` on a non-2xx or a null body.
 */
export async function listForecastsById(ids: string[]): Promise<{
  ok: boolean;
  forecasts: ForecastWire[];
}> {
  const params = new URLSearchParams();
  for (const id of ids) params.append("id", id);
  const r = await apiGet(`/api/forecasts/by-id?${params.toString()}`);
  if (!r.ok || r.body == null) return { ok: false, forecasts: [] };
  const d = r.body as { forecasts?: unknown };
  return { ok: true, forecasts: Array.isArray(d.forecasts) ? (d.forecasts as ForecastWire[]) : [] };
}

/**
 * Deliberately its own function, not a `listForecasts` option: the two are different
 * endpoints with different pagination/response shapes, and keeping them separate is what
 * lets the shared lane load independently without ever gating on (or being conflated with)
 * the owner leg's hydration. `cursor` round-trips the opaque `next_page_token` the server
 * hands back. The view defaults to lean server-side, the opposite of the owner leg, which
 * defaults to full.
 */
export async function listSharedForecasts(
  opts: { view?: "full" | "lean"; limit?: number; cursor?: string | null } = {},
): Promise<{
  ok: boolean;
  forecasts: SharedForecastWire[];
  // The VIEWER's own watchlist membership for these rows, keyed exactly as the owner
  // leg's is.
  pinned: Record<string, unknown>;
  nextPageToken: string | null;
}> {
  const params = new URLSearchParams();
  if (opts.view) params.set("view", opts.view);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.cursor) params.set("next_page_token", opts.cursor);
  const qs = params.toString();
  const r = await apiGet(`/api/forecasts/shared${qs ? `?${qs}` : ""}`);
  if (!r.ok || r.body == null) return { ok: false, forecasts: [], pinned: {}, nextPageToken: null };
  const d = r.body as {
    forecasts?: SharedForecastWire[];
    pinned?: Record<string, unknown>;
    next_page_token?: string | null;
  };
  return {
    ok: true,
    forecasts: d.forecasts || [],
    // `{}` for a server that predates the key, so an older deployment reads as "nothing
    // watchlisted" rather than undefined reaching the pin map.
    pinned: d.pinned || {},
    nextPageToken: d.next_page_token ?? null,
  };
}

/**
 * The server allocates `forecastId`, so it is optional. `contractRowKey` is the row's contract
 * key after the save (null when it has none yet). `status` is the answer's HTTP status (0 for
 * a network loss), and `error` the refusal's code (`not_started`, `derived_frozen`, …) when
 * the server named one, so a caller can name it in its report or leave an expected one
 * uncounted. Best-effort; never throws.
 */
export async function saveForecast(
  row: SavableForecast,
  options: IntentOptions = {},
): Promise<{
  ok: boolean;
  status: number;
  forecastId?: string;
  contractRowKey?: string | null;
  error?: string;
}> {
  const r = await apiPost("/api/forecasts", row, { intentId: options.intentId });
  const refusal = !r.ok ? (r.body as { error?: unknown } | null) : null;
  // Only surface the ids on success — a caller (branch create) should never act on an id that
  // leaked through an error body.
  const body = r.ok
    ? (r.body as { forecastId?: string; contract_row_key?: string | null } | null)
    : null;
  return {
    ok: r.ok,
    status: r.status,
    forecastId: body?.forecastId,
    contractRowKey: typeof body?.contract_row_key === "string" ? body.contract_row_key : null,
    error: typeof refusal?.error === "string" ? refusal.error : undefined,
  };
}

/** Persist a completed branch derived from one machine trigger, without starting a run. */
export async function createDerivedBranch(
  parentForecastId: string,
  body: Record<string, unknown>,
  options: { signal?: AbortSignal } & IntentOptions = {},
): Promise<{ ok: boolean; status: number; row: ForecastWire | null; error: string | null }> {
  const r = await apiPost(
    `/api/forecasts/${encodeURIComponent(parentForecastId)}/derived-branch`,
    body,
    options,
  );
  const response =
    r.body && typeof r.body === "object" ? (r.body as Record<string, unknown>) : null;
  return {
    ok: r.ok && response != null,
    status: r.status,
    row: r.ok && response != null ? (response as unknown as ForecastWire) : null,
    error: !r.ok && typeof response?.error === "string" ? response.error : null,
  };
}

/**
 * The server generates at most once per branch; a `row` is the branch read back from the
 * store after that write, `pending` means another request holds the generation and a later
 * call will read its result.
 */
export async function generateDerivedBranchTriggers(
  forecastId: string,
  options: { signal?: AbortSignal; onUnauthorized?: () => void } & IntentOptions = {},
): Promise<{ ok: boolean; status: number; row: ForecastWire | null; pending: boolean }> {
  const r = await apiPost(
    `/api/forecasts/${encodeURIComponent(forecastId)}/branch-triggers`,
    {},
    options,
  );
  const response =
    r.body && typeof r.body === "object" ? (r.body as Record<string, unknown>) : null;
  const pending = r.status === 202;
  const row = r.ok && !pending && response != null ? (response as unknown as ForecastWire) : null;
  return { ok: r.ok, status: r.status, row, pending };
}

/**
 * A report read the server refused (`!ok`) folds to the same null as a report of the wrong
 * shape, so the terminal outcome is warned here, where the status is still known. Quiet for
 * the read whose own `signal` aborted it: the derive's 401 handler aborts on purpose (the
 * session is gone, not the report), and `apiGet` then resolves `{ok:false,status:0}`.
 */
function warnReportReadFailed(status: number, signal: AbortSignal | undefined): void {
  if (signal?.aborted) return;
  console.warn(
    "forecast_report.read_failed",
    new Error(`GET /api/forecast/report answered status ${status}`),
  );
}

/** Read the authoritative binary headline attached to one stored forecast pointer.
 *  `onUnauthorized` overrides the client's default 401 handling (a redirect to sign-in) for
 *  a background read that must not act on the session's behalf. */
export async function getForecastReportHeadline(
  rowKey: string,
  options: { signal?: AbortSignal; onUnauthorized?: () => void } & IntentOptions = {},
): Promise<number | null> {
  const r = await apiGet(`/api/forecast/report?row_key=${encodeURIComponent(rowKey)}`, options);
  if (!r.ok) {
    warnReportReadFailed(r.status, options.signal);
    return null;
  }
  const value = (r.body as { forecast_value?: unknown } | null)?.forecast_value as
    | {
        outcome_space?: { kind?: unknown };
        representation?: { form?: unknown; mass?: unknown };
      }
    | undefined;
  if (value?.outcome_space?.kind !== "boolean") return null;
  if (value.representation?.form !== "pmf") return null;
  const mass = value.representation.mass;
  if (!Array.isArray(mass) || mass.length !== 2) return null;
  if (mass.some((slot) => typeof slot !== "number" || !Number.isFinite(slot) || slot < 0)) {
    return null;
  }
  if (Math.abs(mass[0] + mass[1] - 1) > 1e-9) return null;
  return mass[1];
}

/** Returns the stored pointer's headline mass when its answer is a coherent distribution
 *  over a non-boolean space, else null. */
export async function getForecastReportMass(
  rowKey: string,
  options: { signal?: AbortSignal; onUnauthorized?: () => void } & IntentOptions = {},
): Promise<number[] | null> {
  const r = await apiGet(`/api/forecast/report?row_key=${encodeURIComponent(rowKey)}`, options);
  if (!r.ok) {
    warnReportReadFailed(r.status, options.signal);
    return null;
  }
  const value = (r.body as { forecast_value?: unknown } | null)?.forecast_value as
    | {
        outcome_space?: OutcomeSpace;
        representation?: { form?: unknown; mass?: unknown };
      }
    | undefined;
  const space = value?.outcome_space;
  if (!space || typeof space !== "object" || space.kind === "boolean") return null;
  if (value.representation?.form !== "pmf") return null;
  const mass = value.representation.mass;
  if (!Array.isArray(mass)) return null;
  try {
    validatePmf(mass, space);
  } catch {
    return null;
  }
  return [...mass];
}

/** Delete cascades to the forecast's branch rows. Best-effort; never throws. */
export async function deleteForecast(id: string, options: IntentOptions = {}): Promise<void> {
  await apiDelete(`/api/forecasts/${encodeURIComponent(id)}`, { intentId: options.intentId });
}

/**
 * A separate route because plain delete is owner-only: a collaborator's delete is
 * scoped to branches, and is authorized against the branch's PARENT (the same check
 * branch creation makes), never against ownership of the row. Never throws.
 */
export async function deleteSharedBranch(id: string, options: IntentOptions = {}): Promise<void> {
  await apiDelete(`/api/forecasts/${encodeURIComponent(id)}/branch`, {
    intentId: options.intentId,
  });
}

/**
 * Archives the branch (and its own branches) rather than deleting it, so a past mark can
 * still draw it. `shared` addresses a collaborator's branch by its pg UUID
 * (`/branch/archive`); otherwise `id` is the caller's client id. Never throws.
 */
export async function archiveBranch(
  id: string,
  { shared = false, intentId = null }: { shared?: boolean } & IntentOptions = {},
): Promise<{ ok: boolean; status: number | null; clearedAt: string | null }> {
  const path = shared ? "branch/archive" : "archive";
  const r = await apiPost(`/api/forecasts/${encodeURIComponent(id)}/${path}`, undefined, {
    intentId,
  });
  const body = (r?.body ?? null) as { clearedAt?: string } | null;
  return { ok: !!r?.ok, status: r?.status ?? null, clearedAt: body?.clearedAt ?? null };
}

/** Soft-cancel (not a hard kill). Never throws; `ok` says whether the server took it. */
export async function cancel(
  traceId: string,
  options: IntentOptions = {},
): Promise<{ ok: boolean }> {
  const r = await apiPost(`/api/forecasts/${encodeURIComponent(traceId)}/cancel`, undefined, {
    intentId: options.intentId,
  });
  return { ok: !!r?.ok };
}

/**
 * The caller branches on `status` for the load-bearing 404-recovery (404 = "still running /
 * stochastic per-instance miss"). A 2xx with a null body (bad JSON) is reported as `ok:false`
 * so a caller never sees a `null` ForecastResult.
 */
export async function getResult(
  traceId: string,
  options: { signal?: AbortSignal } & IntentOptions = {},
): Promise<{ ok: boolean; status: number; result?: ForecastResult }> {
  const r = await apiGet(`/api/forecasts/${encodeURIComponent(traceId)}/result`, options);
  if (r.ok && r.body == null) return { ok: false, status: r.status };
  return {
    ok: r.ok,
    status: r.status,
    result: r.ok ? (r.body as ForecastResult) : undefined,
  };
}

/**
 * `POST /api/forecasts/:id/premise/resolve`: ask the resolver now about a branch's premise
 * trigger (`forecastId` is the branch's server id). Never throws; `traceId` is null unless the
 * resolver took the request.
 */
export async function submitPremiseCheck(
  id: string,
  forecastId: string,
  options: { signal?: AbortSignal } & IntentOptions = {},
): Promise<{ ok: boolean; status: number; traceId: string | null }> {
  const r = await apiPost(
    `/api/forecasts/${encodeURIComponent(id)}/premise/resolve`,
    { forecastId },
    options,
  );
  const traceId = (r?.body as { trace_id?: unknown } | null)?.trace_id;
  return {
    ok: !!r?.ok,
    status: r?.status ?? 0,
    traceId: typeof traceId === "string" && traceId ? traceId : null,
  };
}

/**
 * `GET /api/forecasts/:id/premise/resolve/:traceId`: poll a branch premise check. `status` is
 * the resolver's (`in_flight` while it runs); once it settles, `premise` is the branch's premise
 * record after grading. Never throws.
 */
export async function pollPremiseCheck(
  id: string,
  traceId: string,
  forecastId: string,
  options: { signal?: AbortSignal } & IntentOptions = {},
): Promise<{ ok: boolean; status: number; verdict: string | null; premise: unknown }> {
  const r = await apiGet(
    `/api/forecasts/${encodeURIComponent(id)}/premise/resolve/${encodeURIComponent(
      traceId,
    )}?forecastId=${encodeURIComponent(forecastId)}`,
    options,
  );
  const body = (r?.body ?? null) as { status?: unknown; premise?: unknown } | null;
  return {
    ok: !!r?.ok,
    status: r?.status ?? 0,
    verdict: typeof body?.status === "string" ? body.status : null,
    premise: body?.premise ?? null,
  };
}

/**
 * THROWS a descriptive error on a non-ok / null-body response so submit + editResubmit keep
 * their try/catch + `signal.aborted` control flow (an abort surfaces as `{ok:false,status:0}` →
 * a thrown error → the caller's `signal.aborted` check distinguishes it from a real failure).
 */
export async function preprocess(
  engine: string,
  questionText: string,
  opts: {
    signal?: AbortSignal | null;
    mode?: "binary" | "auto" | "multi";
    /**
     * A registered preprocess-variant id to author with, from the debug picker. Sent
     * ONLY on the `"auto"` mode — a variant IS an auto authoring strategy, so the other
     * two endpoints have nothing to dispatch on. Honoured only in a deployment whose
     * debug surface is available (server-side gate: EXPOSE_DEBUG_UI + the
     * `chatboard-debug-ui` flag), so a value here is a request, never a guarantee. Empty
     * / omitted leaves the deployment default in charge.
     */
    variantId?: string | null;
    /** The chat turn id is used for timing telemetry only (lib/chatTurn.js). */
    turnId?: string | null;
  } & IntentOptions = {},
): Promise<PreprocessedContract> {
  // All three endpoints return either the plain binary contract or the multi superset
  // (+ outcome_space/outcome_labels), so downstream keys only on outcome_space presence.
  // The "auto" endpoint classifies the question itself and returns outcome_space only when
  // the answer is genuinely multi-valued.
  const suffix =
    opts.mode === "multi"
      ? "/preprocess/multi"
      : opts.mode === "auto"
        ? "/preprocess/auto"
        : "/preprocess";
  const endpoint = `/api/forecast${suffix}`;
  const variantId = opts.mode === "auto" && opts.variantId ? opts.variantId : "";
  // The reader's IANA zone. Deadlines are stated in a human calendar — "by EOD
  // today" means THEIR day — so the server needs it to read the question the way
  // it was meant. Wrapped because Intl can be unavailable in a hardened runtime,
  // and a missing zone must cost a correct deadline, never the submit.
  let timezone = "";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    /* no Intl — the server falls back to UTC and labels it a fallback */
  }
  const payload = {
    orchestrator: engine,
    question: { question_text: questionText },
    // Omitted rather than empty when unknown, so the server can distinguish "no
    // zone" from a zone it was given.
    ...(timezone && { timezone }),
    // Omitted rather than empty when there is no override: the server distinguishes "no
    // opinion" (use the deployment default) from a value it must gate and validate, and
    // an empty string would enter that second path for no reason.
    ...(variantId && { variant_id: variantId }),
  };
  const r = await apiPost(endpoint, payload, {
    signal: opts.signal,
    headers: chatTurnHeaders(opts.turnId),
    intentId: opts.intentId,
  });
  if (!r.ok) {
    const d = (r.body as { error?: string; detail?: string } | null) || {};
    throw new Error(d.error || d.detail || `Preprocess failed (${r.status})`);
  }
  if (r.body == null) throw new Error(`Preprocess returned ${r.status} with non-JSON body`);
  return r.body as PreprocessedContract;
}

/**
 * One authoring strategy as `GET /api/forecast/preprocess/variants` advertises it.
 * Mirrors `contract_server.eval.VariantSummary`; ids, labels and model names only —
 * prompt text is never served, which is what keeps this listing safe to hand a browser.
 */
export interface PreprocessVariantSummary {
  id: string;
  label: string;
  description: string;
  model: string;
  temperature: number;
  web_engine: string | null;
  is_default: boolean;
}

/**
 * `deployment_default` is this deployment's flag value; "" means the contract server's own
 * registry default.
 *
 * Returns `null` rather than throwing on ANY non-ok, including the 404 a deployment
 * without a debug surface answers with, which is the ordinary case in prod and not an
 * error worth surfacing. The picker renders nothing when this is null.
 */
export async function getPreprocessVariants(): Promise<{
  variants: PreprocessVariantSummary[];
  deploymentDefault: string;
} | null> {
  const r = await apiGet("/api/forecast/preprocess/variants");
  if (!r.ok || r.body == null) return null;
  const body = r.body as { variants?: unknown; deployment_default?: unknown };
  if (!Array.isArray(body.variants)) return null;
  return {
    variants: body.variants as PreprocessVariantSummary[],
    deploymentDefault: typeof body.deployment_default === "string" ? body.deployment_default : "",
  };
}

/**
 * Wake the forecast server (cold start) ahead of a preprocess retry. `_engine` is unused
 * (there is one forecast backend); it stays so the options remain the second argument,
 * which intentDeps.ts indexes.
 */
export async function health(
  _engine: string,
  opts: {
    /** The chat turn id is used for timing telemetry only (lib/chatTurn.js). */
    turnId?: string | null;
  } & IntentOptions = {},
): Promise<void> {
  await apiGet("/api/forecast/health", {
    headers: chatTurnHeaders(opts.turnId),
    intentId: opts.intentId,
  });
}

/** A non-2xx response or a 2xx with a null body yields `ok:false`, so the caller keeps the
 *  last-good snapshot rather than overwriting it with null. */
/**
 * Copy one of Sayer's forecasts (a catalogue question) into the user's forecasts, as `clientId`,
 * or return the copy they already have of it. Never throws.
 */
export async function copySayerForecast(
  questionKey: string,
  clientId: string,
  options: IntentOptions = {},
): Promise<{ ok: boolean; row: ForecastWire | null; error: string | null }> {
  const r = await apiPost(
    "/api/sayer-forecasts/copy",
    { questionKey, clientId },
    { intentId: options.intentId },
  );
  const body = r.body && typeof r.body === "object" ? (r.body as Record<string, unknown>) : null;
  const row = r.ok && body?.forecast ? (body.forecast as ForecastWire) : null;
  return {
    ok: row != null,
    row,
    error: row ? null : typeof body?.error === "string" ? body.error : `HTTP ${r.status}`,
  };
}

/**
 * Make a copy of one of Sayer's forecasts the user's own: Sayer's updates stop applying to it.
 * `converted` is false when it already was theirs. Never throws.
 */
export async function adoptSayerForecast(
  clientId: string,
  options: IntentOptions = {},
): Promise<{ ok: boolean; row: ForecastWire | null; converted: boolean; error: string | null }> {
  const r = await apiPost(
    "/api/sayer-forecasts/copy/convert",
    { clientId },
    { intentId: options.intentId },
  );
  const body = r.body && typeof r.body === "object" ? (r.body as Record<string, unknown>) : null;
  const row = r.ok && body?.forecast ? (body.forecast as ForecastWire) : null;
  return {
    ok: row != null,
    row,
    converted: row != null && body?.converted === true,
    error: row ? null : typeof body?.error === "string" ? body.error : `HTTP ${r.status}`,
  };
}

export async function getCalibration(): Promise<{ ok: boolean; body: unknown }> {
  const r = await apiGet("/api/calibration");
  return { ok: r.ok && r.body != null, body: r.body };
}

export interface ForecastApi {
  listForecasts: typeof listForecasts;
  listForecastsByContract: typeof listForecastsByContract;
  listForecastsById: typeof listForecastsById;
  listSharedForecasts: typeof listSharedForecasts;
  saveForecast: typeof saveForecast;
  createDerivedBranch: typeof createDerivedBranch;
  generateDerivedBranchTriggers: typeof generateDerivedBranchTriggers;
  getForecastReportHeadline: typeof getForecastReportHeadline;
  getForecastReportMass: typeof getForecastReportMass;
  deleteForecast: typeof deleteForecast;
  deleteSharedBranch: typeof deleteSharedBranch;
  archiveBranch: typeof archiveBranch;
  cancel: typeof cancel;
  getResult: typeof getResult;
  submitPremiseCheck: typeof submitPremiseCheck;
  pollPremiseCheck: typeof pollPremiseCheck;
  preprocess: typeof preprocess;
  getPreprocessVariants: typeof getPreprocessVariants;
  health: typeof health;
  getCalibration: typeof getCalibration;
  copySayerForecast: typeof copySayerForecast;
  adoptSayerForecast: typeof adoptSayerForecast;
}

/** The production instance wired into `makeDefaultDeps().services.api`; tests substitute fakes. */
export const forecastApi: ForecastApi = {
  listForecasts,
  listForecastsByContract,
  listForecastsById,
  listSharedForecasts,
  saveForecast,
  createDerivedBranch,
  generateDerivedBranchTriggers,
  getForecastReportHeadline,
  getForecastReportMass,
  deleteForecast,
  deleteSharedBranch,
  archiveBranch,
  cancel,
  getResult,
  submitPremiseCheck,
  pollPremiseCheck,
  preprocess,
  getPreprocessVariants,
  health,
  getCalibration,
  copySayerForecast,
  adoptSayerForecast,
};
